import React from 'react';

import { LocalDate } from '@ha/utilities/Dates/LocalDate';

import { extendReactComponent } from 'ha/utilities/extendReactComponent';

import { ListingCardModel } from 'ha/models/Listing/types';

import { ListingCardBase } from './ListingCardBase';
import { ListingCardButtonFavorite } from './ListingCardButtonFavorite';
import { ListingCardContext } from './ListingCardContext';
import { ListingCardDetails } from './ListingCardDetails/ListingCardDetails';
import { ListingCardDetailsAvailability } from './ListingCardDetails/ListingCardDetailsAvailability';
import {
  ListingCardDetailsPrice,
  usePriceInformation,
} from './ListingCardDetails/ListingCardDetailsPrice';
import { ListingCardDetailsRating } from './ListingCardDetails/ListingCardDetailsRating';
import { ListingCardDetailsTitle } from './ListingCardDetails/ListingCardDetailsTitle';
import { ListingCardDetailsAttributes } from './ListingCardDetailsAttributes/ListingCardDetailsAttributes';
import {
  ListingCardDetailsAttributesPlaces,
  shouldShowListingCardDetailsAttributesPlaces,
} from './ListingCardDetailsAttributes/ListingCardDetailsAttributesPlaces';
import {
  ListingCardDetailsAttributesSize,
  shouldShowListingCardDetailsAttributesSize,
} from './ListingCardDetailsAttributes/ListingCardDetailsAttributesSize';
import { ListingCardGallery } from './ListingCardGallery/ListingCardGallery';
import { ListingCardGallerySummary } from './ListingCardGallery/ListingCardGallerySummary';
import { ListingCardGallerySummaryTracked } from './ListingCardGallery/ListingCardGallerySummaryTracked';
import { buildListingCardHighlightElements } from './ListingCardHighlight/buildListingCardHighlightElements';
import { ListingCardHighlight } from './ListingCardHighlight/ListingCardHighlight';
import { ListingCardHighlightConfirmed } from './ListingCardHighlight/ListingCardHighlightsConfirmed';
import { ListingCardHighlightExclusive } from './ListingCardHighlight/ListingCardHighlightsExclusive';
import { ListingCardHighlightFlexibleCancellation } from './ListingCardHighlight/ListingCardHighlightsFlexibleCancellation';
import { ListingCardHighlightNew } from './ListingCardHighlight/ListingCardHighlightsNew';
import { ListingCardHighlightNoDeposit } from './ListingCardHighlight/ListingCardHighlightsNoDeposit';
import { ListingCardHighlightZeroRisk } from './ListingCardHighlight/ListingCardHighlightsZeroRisk';
import { ListingCardBaseProps } from './types';
import { compareRecordLikeProps } from './utilities/compareRecordLikeProps';

interface ListingCardUnitTypeHighlightsProps {
  listing: ListingCardModel;
  isSimilarListing?: boolean;
}

const ListingCardUnitTypeHighlights = ({
  listing,
  isSimilarListing = false,
}: ListingCardUnitTypeHighlightsProps) => {
  const showHighlightNew = ListingCardHighlightNew.useShow({
    listing,
  });
  const showHighlightExclusive = ListingCardHighlightExclusive.useShow({
    listing,
  });
  const showHighlightNoDeposit = ListingCardHighlightNoDeposit.useShow({
    listing,
  });

  const showHighlightFlexibleCancellation =
    ListingCardHighlightFlexibleCancellation.useShow({ listing });
  const showHighlightConfirmed = ListingCardHighlightConfirmed.useShow({
    listing,
  });
  const showHighlightZeroRisk = ListingCardHighlightZeroRisk.useShow({
    listing,
  });

  const highlights = React.useMemo(
    () =>
      buildListingCardHighlightElements({
        showHighlightSimilarHome: isSimilarListing,
        showHighlightConfirmed: Boolean(showHighlightConfirmed),
        showHighlightNoDeposit: Boolean(showHighlightNoDeposit),
        showHighlightFlexibleCancellation: Boolean(
          showHighlightFlexibleCancellation,
        ),
        showHighlightZeroRisk: Boolean(showHighlightZeroRisk),
        showHighlightNew: Boolean(showHighlightNew),
        showHighlightExclusive: Boolean(showHighlightExclusive),
      }),
    [
      isSimilarListing,
      showHighlightConfirmed,
      showHighlightNoDeposit,
      showHighlightFlexibleCancellation,
      showHighlightZeroRisk,
      showHighlightNew,
      showHighlightExclusive,
    ],
  );

  return <ListingCardHighlight>{highlights}</ListingCardHighlight>;
};

export interface ListingCardUnitTypeProps extends ListingCardBaseProps {
  className?: {
    root?: string;
    card?: string;
  };
}

const ListingCardUnitTypeBase = React.memo(
  ({
    listing,
    trackAttributes,
    startDate,
    endDate,
    className,
    onMouseEnter,
    onMouseLeave,
    slots,
    isSimilarListing = false,
    size = 'lg',
    disableGallerySwipe = false,
    isLazy = false,
  }: ListingCardUnitTypeProps) => {
    const defaultedStartDate = React.useMemo(
      () => startDate ?? LocalDate.today(),
      [startDate],
    );

    const priceInformation = usePriceInformation({
      listing,
      startDate: defaultedStartDate,
      endDate,
    });

    const componentGalleryButtonFavorite = React.useMemo(
      () => (
        <ListingCardButtonFavorite
          listing={listing}
          trackAttributes={{
            cardLocation: trackAttributes.cardLocation,
          }}
        />
      ),
      [listing, trackAttributes],
    );

    const componentGalleryHighlight = React.useMemo(
      () => (
        <ListingCardUnitTypeHighlights
          listing={listing}
          isSimilarListing={isSimilarListing}
        />
      ),
      [listing, isSimilarListing],
    );

    const renderComponentGallerySummary = React.useCallback(
      ({ children }: { children: React.ReactNode }) => (
        <ListingCardGallerySummaryTracked
          listing={listing}
          trackAttributes={{
            cardLocation: trackAttributes.cardLocation,
            cardPosition: trackAttributes.cardPosition,
          }}
        >
          <ListingCardGallerySummary listing={listing}>
            {children}
          </ListingCardGallerySummary>
        </ListingCardGallerySummaryTracked>
      ),
      [listing, trackAttributes.cardLocation, trackAttributes.cardPosition],
    );

    const context = React.useMemo(
      () => ({ size, disableGallerySwipe }),
      [size, disableGallerySwipe],
    );

    return (
      <ListingCardContext.Provider value={context}>
        <ListingCardBase.Container className={className?.root}>
          <ListingCardBase
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            className={className?.card}
          >
            <ListingCardGallery
              listing={listing}
              isLazy={isLazy}
              slots={{
                summary: renderComponentGallerySummary,
                cornerTopRight:
                  slots?.galleryAction ?? componentGalleryButtonFavorite,
                cornerTopLeft: componentGalleryHighlight,
                cornerBottomLeft: slots?.galleryAnnotation,
              }}
            />

            <ListingCardDetails
              slots={{
                header: <ListingCardDetailsTitle listing={listing} />,
                corner: <ListingCardDetailsRating listing={listing} />,
                footer: <ListingCardDetailsAvailability listing={listing} />,
              }}
            >
              <ListingCardDetailsAttributes>
                {shouldShowListingCardDetailsAttributesSize(listing) && (
                  <ListingCardDetailsAttributesSize listing={listing} />
                )}
                {shouldShowListingCardDetailsAttributesPlaces(listing) && (
                  <ListingCardDetailsAttributesPlaces listing={listing} />
                )}
              </ListingCardDetailsAttributes>

              <ListingCardDetailsPrice
                priceInformation={priceInformation}
                listing={listing}
              />
            </ListingCardDetails>
          </ListingCardBase>
        </ListingCardBase.Container>
      </ListingCardContext.Provider>
    );
  },
  compareRecordLikeProps(['className', 'slots', 'trackAttributes']),
);

export const ListingCardUnitType = extendReactComponent(
  ListingCardUnitTypeBase,
  {
    Highlights: ListingCardUnitTypeHighlights,
  },
);
